// Lets a visitor choose how many products show per page (24/48/96) on any collection grid,
// entirely client-side. Liquid's {% paginate %} tag can't read a custom URL param for its "by"
// count (Shopify doesn't expose arbitrary query-string values to Liquid), so true server-side
// per-page selection isn't possible without a full page reload per collection per page-size.
// Instead, every collection template's own products_per_page setting is 96 (REAL_BATCH_SIZE
// below, matching the largest selectable option) — the real Shopify-level "page" already
// contains up to 96 products — and this script re-slices that same batch into smaller local
// pages, with zero extra requests as long as the target page falls within what's already loaded.
//
// Pagination always shows the TRUE total page count for the whole collection (ceil(totalCount /
// pageSize) — e.g. 123 products at 24/page is 6 pages), not just how many fit in the currently
// loaded batch. Clicking a page outside that batch does a real navigation to the Shopify page
// (?page=N) that contains it, and — via sessionStorage — lands directly on the right local
// sub-page within that fresh batch instead of resetting to page 1.
(() => {
  const STORAGE_KEY = 'jsc:collectionPageSize';
  const TARGET_LOCAL_PAGE_KEY = 'jsc:targetLocalPage';
  const LOCAL_PAGE_PARAM = 'local_page';
  const VALID_SIZES = [24, 48, 96];
  const DEFAULT_SIZE = 24;
  const REAL_BATCH_SIZE = 96;

  const getPageSize = () => {
    const stored = Number(window.localStorage.getItem(STORAGE_KEY));
    return VALID_SIZES.includes(stored) ? stored : DEFAULT_SIZE;
  };

  const setPageSize = (size) => {
    window.localStorage.setItem(STORAGE_KEY, String(size));
  };

  // Per-container state: the currently selected local page, and the MutationObserver watching
  // that container (disconnected while this script makes its own DOM changes, so rebuilding the
  // pagination nav or hiding/showing <li> items doesn't re-trigger the very observer that's
  // meant to detect facets.js's *own* AJAX re-renders — without that guard, every apply() would
  // queue another apply() forever).
  const stateByContainer = new Map();

  // localPage is the local page this cross-batch navigation is actually headed to - without
  // setting it here too, this URL would otherwise carry over whatever local_page happened to
  // already be in the address bar (e.g. still "4" from a previous local-only page change via
  // updateLocalPageInUrl), and getLocalPageFromUrl() prefers that stale URL value over the fresh
  // sessionStorage flag on the reloaded page, landing back on the old page instead of the one
  // that was actually clicked.
  const realPageUrlFor = (realPage, localPage) => {
    const url = new URL(window.location.href);
    if (realPage <= 1) {
      url.searchParams.delete('page');
    } else {
      url.searchParams.set('page', String(realPage));
    }
    if (localPage <= 1) {
      url.searchParams.delete(LOCAL_PAGE_PARAM);
    } else {
      url.searchParams.set(LOCAL_PAGE_PARAM, String(localPage));
    }
    return url.toString();
  };

  const getLocalPageFromUrl = () => {
    const value = Number(new URLSearchParams(window.location.search).get(LOCAL_PAGE_PARAM));
    return value > 0 ? value : null;
  };

  // Local page changes (unlike real Shopify pages) never triggered a navigation, so nothing
  // recorded which local page a visitor was on — a full reload (e.g. the browser re-fetching this
  // page after Back from a product) always restarted at local page 1. Mirroring the current local
  // page into the URL via replaceState (no new history entries, just like Shopify's own ?page=N)
  // lets that reload/back-navigation land back on the right page.
  const updateLocalPageInUrl = (localPage) => {
    const url = new URL(window.location.href);
    if (localPage <= 1) {
      url.searchParams.delete(LOCAL_PAGE_PARAM);
    } else {
      url.searchParams.set(LOCAL_PAGE_PARAM, String(localPage));
    }
    if (url.toString() !== window.location.href) {
      history.replaceState(history.state, '', url.toString());
    }
  };

  const buildPaginationNav = (container, localPage, localTotalPages, onGoTo) => {
    const existing = container.querySelector('[data-local-pagination]');
    if (existing) existing.remove();
    if (localTotalPages <= 1) return;

    const wrapper = document.createElement('div');
    wrapper.className = 'pagination-wrapper';
    wrapper.setAttribute('data-local-pagination', '');

    const nav = document.createElement('nav');
    nav.className = 'pagination';
    nav.setAttribute('role', 'navigation');
    nav.setAttribute('aria-label', 'Pagination');

    const list = document.createElement('ul');
    list.className = 'pagination__list list-unstyled';
    list.setAttribute('role', 'list');

    const arrowSvg =
      '<span class="svg-wrapper"><svg viewBox="0 0 12 7" fill="none"><path d="M1 1l5 5 5-5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg></span>';

    const addArrow = (isPrev) => {
      const canGo = isPrev ? localPage > 1 : localPage < localTotalPages;
      if (!canGo) return;
      const li = document.createElement('li');
      const a = document.createElement('a');
      a.href = '#';
      a.className = `pagination__item pagination__item--${isPrev ? 'next' : 'prev'} pagination__item-arrow link motion-reduce`;
      a.style.transform = isPrev ? 'rotate(90deg)' : 'rotate(-90deg)';
      a.setAttribute('aria-label', isPrev ? 'Previous page' : 'Next page');
      a.innerHTML = arrowSvg;
      a.addEventListener('click', (event) => {
        event.preventDefault();
        onGoTo(localPage + (isPrev ? -1 : 1));
      });
      li.appendChild(a);
      list.appendChild(li);
    };

    addArrow(true);

    for (let i = 1; i <= localTotalPages; i += 1) {
      const li = document.createElement('li');
      if (i === localPage) {
        const span = document.createElement('a');
        span.className = 'pagination__item pagination__item--current light';
        span.setAttribute('role', 'link');
        span.setAttribute('aria-disabled', 'true');
        span.setAttribute('aria-current', 'page');
        span.textContent = String(i);
        li.appendChild(span);
      } else {
        const a = document.createElement('a');
        a.href = '#';
        a.className = 'pagination__item link';
        a.setAttribute('aria-label', `Page ${i}`);
        a.textContent = String(i);
        a.addEventListener('click', (event) => {
          event.preventDefault();
          onGoTo(i);
        });
        li.appendChild(a);
      }
      list.appendChild(li);
    }

    addArrow(false);

    nav.appendChild(list);
    wrapper.appendChild(nav);
    container.appendChild(wrapper);
  };

  const applyToContainer = (container) => {
    const grid = container.querySelector('#product-grid');
    if (!grid) return;
    const items = Array.from(grid.children).filter((el) => el.tagName === 'LI');
    if (items.length === 0) return;

    const state = stateByContainer.get(container);
    if (state && state.observer) state.observer.disconnect();

    const pageSize = getPageSize();
    const totalCount = Number(grid.dataset.totalCount) || items.length;
    const currentRealPage = Number(grid.dataset.currentPage) || 1;
    const localTotalPages = Math.max(1, Math.ceil(totalCount / pageSize));

    let localPage = (state && state.localPage) || 1;
    if (localPage > localTotalPages) localPage = localTotalPages;

    // Which real Shopify batch (of REAL_BATCH_SIZE) does this local page's item range fall in?
    const targetRealPage = Math.floor(((localPage - 1) * pageSize) / REAL_BATCH_SIZE) + 1;

    if (targetRealPage !== currentRealPage) {
      // Not in the batch that's actually loaded on this request — do a real navigation to fetch
      // it, remembering which local page to land on once that fresh batch renders.
      window.sessionStorage.setItem(TARGET_LOCAL_PAGE_KEY, String(localPage));
      window.location.href = realPageUrlFor(targetRealPage, localPage);
      return;
    }

    // Offset of this local page's first item within the currently-loaded batch (not within the
    // whole collection) — e.g. loaded batch is items 97-123, local page covering items 97-120
    // starts at offset 0 within that batch, not offset 96.
    const batchStartIndex = (currentRealPage - 1) * REAL_BATCH_SIZE;
    const start = (localPage - 1) * pageSize - batchStartIndex;
    const end = start + pageSize;
    items.forEach((item, index) => {
      item.hidden = index < start || index >= end;
    });

    // Hide Shopify's own server-rendered pagination — this script builds its own that reflects
    // the locally-selected page size instead of the fixed 96-per-real-page count.
    const realPagination = container.querySelector('.pagination-wrapper:not([data-local-pagination])');
    if (realPagination) realPagination.hidden = true;

    updateLocalPageInUrl(localPage);

    buildPaginationNav(container, localPage, localTotalPages, (targetPage) => {
      stateByContainer.set(container, { ...stateByContainer.get(container), localPage: targetPage });
      applyToContainer(container);
      container.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });

    stateByContainer.set(container, { ...stateByContainer.get(container), localPage });
    if (state && state.observer) state.observer.observe(container, { childList: true, subtree: true });
  };

  const applyAll = () => {
    document.querySelectorAll('#ProductGridContainer').forEach((container) => applyToContainer(container));
  };

  const syncButtons = () => {
    const size = getPageSize();
    document.querySelectorAll('[data-page-size-option]').forEach((button) => {
      const isActive = Number(button.dataset.pageSizeOption) === size;
      button.setAttribute('aria-pressed', isActive ? 'true' : 'false');
    });
  };

  const init = () => {
    const sessionPendingLocalPage = Number(window.sessionStorage.getItem(TARGET_LOCAL_PAGE_KEY));
    window.sessionStorage.removeItem(TARGET_LOCAL_PAGE_KEY);
    // The URL wins over the sessionStorage flag: it reflects a normal page load/reload/back-
    // navigation, whereas the sessionStorage flag only exists for the one-shot cross-batch
    // redirect this script itself just triggered via realPageUrlFor().
    const pendingLocalPage = getLocalPageFromUrl() || sessionPendingLocalPage;

    document.querySelectorAll('#ProductGridContainer').forEach((container) => {
      const observer = new MutationObserver(() => {
        // A brand new product list from filters/sort changing (facets.js's own AJAX re-render)
        // — always land back on local page 1 of it, since it's an unrelated result set. The
        // re-render also replaces the page-size buttons with fresh markup (aria-pressed="false"
        // on all of them again), so their active state needs re-syncing too.
        stateByContainer.set(container, { ...stateByContainer.get(container), localPage: 1 });
        syncButtons();
        applyToContainer(container);
      });
      stateByContainer.set(container, { localPage: pendingLocalPage > 0 ? pendingLocalPage : 1, observer });
      observer.observe(container, { childList: true, subtree: true });
    });

    syncButtons();
    applyAll();

    // A cross-batch page click (see applyToContainer's window.location.href branch) or a reload
    // while deep in pagination is a full page load, which the browser always scrolls to the top
    // for - unlike a same-batch page click, which calls scrollIntoView itself (see
    // buildPaginationNav's onGoTo below). Restore that same "land on the product grid" behavior
    // here so paging past the first batch doesn't dump the visitor back at the top of the page.
    if (pendingLocalPage > 1) {
      document.querySelectorAll('#ProductGridContainer').forEach((container) => {
        container.scrollIntoView({ block: 'start' });
      });
    }

    document.addEventListener('click', (event) => {
      const button = event.target.closest('[data-page-size-option]');
      if (!button) return;
      const size = Number(button.dataset.pageSizeOption);
      if (size === getPageSize()) return;
      setPageSize(size);
      document.querySelectorAll('#ProductGridContainer').forEach((container) => {
        stateByContainer.set(container, { ...stateByContainer.get(container), localPage: 1 });
      });
      syncButtons();
      applyAll();
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
